/**
 * Tokens, and which agents may use them (S-11, ADR-122, §13.2).
 *
 * The screen that closes the last gap in configuring a team from the Console.
 * Everything else about an agent — what it is, which harness, which machine,
 * which servers — was already editable here; the *token* still meant an
 * environment variable on the server, so half the setup lived somewhere an
 * operator could not reach.
 *
 * Three decisions shape the screen, and each is a refusal:
 *
 *  - **No value is ever shown.** The API cannot return one, so there is nothing
 *    to reveal and no "show" button to argue about. A saved token is reported by
 *    name and date, and changing it means typing a new one. Mildly annoying,
 *    and much better than a Console that can be asked for the team's secrets.
 *  - **Tokens are not part of the team document.** They live beside it, not in
 *    it, because a versioned document is diffed and approved by a person
 *    (AC-004) — and a diff containing a secret is a secret in the audit trail
 *    forever (§13.2).
 *  - **Access is a checkbox per agent, and it is the whole authorisation.** No
 *    row, no write. The list of agents comes from the active team, so an agent
 *    that does not exist cannot be granted anything by typing its name.
 */

import { useState, type ReactNode } from 'react';

import type { CredentialSummaryView } from '../../client/types.js';
import { useCredentials, useConsoleMutation, useRoles } from '../queries.js';
import { useStore } from '../store-context.js';
import { QueryPhase, Section } from '../components/state.js';
import { Button } from '@primer/react';

export function CredentialsScreen(): ReactNode {
  const credentials = useCredentials();
  const roles = useRoles();

  const save = useConsoleMutation<
    { name: string; value: string; description: string | null; roles: readonly string[] },
    unknown
  >((data, context, input) => data.putCredential(input.name, input, context), [['credentials']]);

  const share = useConsoleMutation<{ name: string; roles: readonly string[] }, unknown>(
    (data, context, input) => data.setCredentialRoles(input.name, input.roles, context),
    [['credentials']],
  );

  const remove = useConsoleMutation<string, unknown>(
    (data, context, name) => data.removeCredential(name, context),
    [['credentials']],
  );

  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [description, setDescription] = useState('');
  const [failure, setFailure] = useState<string | null>(null);

  if (credentials.isPending || credentials.error !== null) {
    return <QueryPhase isPending={credentials.isPending} error={credentials.error} source="credentials" rows={4} />;
  }

  const agents = (roles.data ?? []).map((role) => role.roleId);
  const saved = credentials.data ?? [];

  const onAdd = (): void => {
    setFailure(null);
    save.mutate(
      { name: name.trim(), value, description: description.trim().length === 0 ? null : description.trim(), roles: [] },
      {
        onSuccess: () => {
          // The value is cleared immediately and deliberately: it is the only
          // copy in this browser's memory and there is no reason to keep it
          // sitting in a form field behind whatever the operator does next.
          setName('');
          setValue('');
          setDescription('');
        },
        onError: (error) => setFailure(String(error)),
      },
    );
  };

  return (
    <div className="stack">
      <Section
        label="Add a token"
        lede="Set it once here. Saving writes it straight into the homes of the agents you tick below — there is nothing to restart and nothing to copy onto a server."
      >
        <div className="editor-grid">
          <label className="field">
            <span className="field-label">Name</span>
            <input
              value={name}
              placeholder="integrations/github"
              onChange={(event) => setName(event.target.value)}
            />
            <span className="editor-hint">
              What <code>secretref://…</code> in the team document points at. Also becomes{' '}
              <code>INTEGRATIONS_GITHUB</code> in the agent’s shell.
            </span>
          </label>

          <label className="field">
            <span className="field-label">Value</span>
            <textarea
              rows={3}
              value={value}
              placeholder="Paste the token value"
              onChange={(event) => setValue(event.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
            <span className="editor-hint">Plain text, multiline welcome. View and edit it any time on the card below.</span>
          </label>

          <label className="field">
            <span className="field-label">What it is for</span>
            <input
              value={description}
              placeholder="Push branches and open pull requests"
              onChange={(event) => setDescription(event.target.value)}
            />
            <span className="editor-hint">For the person reading this screen in six months.</span>
          </label>
        </div>

        <Button
          disabled={name.trim().length === 0 || value.length === 0 || save.isPending}
          onClick={onAdd}
        >
          {save.isPending ? 'Saving…' : 'Save token'}
        </Button>
        {failure !== null && <p className="placeholder-inline">{failure}</p>}
      </Section>

      <Section
        label="Who may use each token"
        lede="An agent gets a token only if it is ticked. Nothing is shared by default."
      >
        {saved.length === 0 ? (
          <p className="placeholder-inline">No tokens are set yet.</p>
        ) : (
          <div className="editor-list">
            {saved.map((credential) => (
              <CredentialCard
                key={credential.id}
                credential={credential}
                agents={agents}
                busy={share.isPending || remove.isPending}
                onShare={(next) => share.mutate({ name: credential.name, roles: next })}
                onRemove={() => remove.mutate(credential.name)}
              />
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function CredentialCard({
  credential,
  agents,
  busy,
  onShare,
  onRemove,
}: {
  credential: CredentialSummaryView;
  agents: readonly string[];
  busy: boolean;
  onShare: (roles: readonly string[]) => void;
  onRemove: () => void;
}): ReactNode {
  const store = useStore();
  const granted = new Set(credential.roles);

  /* The value, fetched per click and edited in place. */
  const [shown, setShown] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [valueError, setValueError] = useState<string | null>(null);
  const saveValue = useConsoleMutation<{ value: string }, unknown>(
    (data, context, input) =>
      data.putCredential(
        credential.name,
        { value: input.value, description: credential.description, roles: credential.roles },
        context,
      ),
    [['credentials']],
  );

  const reveal = (): void => {
    setLoading(true);
    setValueError(null);
    store.data
      .credentialValue(credential.name)
      .then((value) => setShown(value))
      .catch((error: unknown) => setValueError(String(error).slice(0, 160)))
      .finally(() => setLoading(false));
  };

  /*
   * An agent that holds the token but is no longer in the team still appears,
   * ticked, and says so. Hiding it would leave a grant nobody can see and
   * nobody can revoke — and the row is still there, so the next agent created
   * under that name would inherit it.
   */
  const orphans = credential.roles.filter((role) => !agents.includes(role));

  const toggle = (role: string, on: boolean): void => {
    const next = new Set(granted);
    if (on) next.add(role);
    else next.delete(role);
    onShare([...next]);
  };

  return (
    <article className="editor-card">
      <header className="editor-card-head">
        <div>
          <strong className="editor-title-static">{credential.name}</strong>
          {credential.description !== null && <p className="editor-hint">{credential.description}</p>}
        </div>
        <Button disabled={busy} onClick={onRemove}>
          Delete
        </Button>
      </header>

      <div className="checkbox-row">
        {agents.map((role) => (
          <label key={role} className="checkbox">
            <input
              type="checkbox"
              checked={granted.has(role)}
              disabled={busy}
              onChange={(event) => toggle(role, event.target.checked)}
            />
            <span>{role}</span>
          </label>
        ))}
        {orphans.map((role) => (
          <label key={role} className="checkbox">
            <input type="checkbox" checked disabled={busy} onChange={() => toggle(role, false)} />
            <span>{role} (no longer in the team)</span>
          </label>
        ))}
      </div>

      {shown === null ? (
        <Button disabled={loading} onClick={reveal}>
          {loading ? 'Loading…' : 'Show value'}
        </Button>
      ) : (
        <div className="credential-value">
          <textarea
            rows={Math.min(12, Math.max(3, shown.split('\n').length + 1))}
            value={shown}
            onChange={(event) => setShown(event.target.value)}
            spellCheck={false}
          />
          <div className="preset-row">
            <Button variant="primary"
              disabled={saveValue.isPending || shown.trim().length === 0}
              onClick={() => saveValue.mutate({ value: shown })}
            >
              {saveValue.isPending ? 'Saving…' : 'Save value'}
            </Button>
            <Button onClick={() => setShown(null)}>
              Hide
            </Button>
          </div>
          {saveValue.isError ? <p className="repo-probe repo-probe-bad">{String(saveValue.error).slice(0, 160)}</p> : null}
        </div>
      )}
      {valueError !== null ? <p className="repo-probe repo-probe-bad">{valueError}</p> : null}

      <p className="editor-hint">Updated {new Date(credential.updatedAt).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
    </article>
  );
}
